import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Add, Messages1 } from 'iconsax-react';

import { Button } from '@design-system/button';
import { createRoute, PATHS } from '@src/core/router/paths';
import { AgentCreateDialog } from '@features/ai-agents/components/agent-create-dialog';
import {
  canManageAgents,
  MANAGE_AGENTS_HINT,
} from '@features/ai-agents/utils/can-manage-agents';
import { useGetMe } from '@shared/services/get-me';
import { KbaseFormDialog } from '@features/knowledge-base/components/kbase-form-dialog';

function knowledgeBaseListPath(pathname: string) {
  const at = pathname.indexOf(PATHS.CONVERSATIONS_KNOWLEDGE_BASE);
  if (at === -1) return pathname;
  return pathname.slice(0, at + PATHS.CONVERSATIONS_KNOWLEDGE_BASE.length);
}

function agentsListPath(pathname: string) {
  const at = pathname.indexOf(PATHS.CONVERSATIONS_AGENTS);
  if (at === -1) return pathname;
  return pathname.slice(0, at + PATHS.CONVERSATIONS_AGENTS.length);
}

export function InboxSystemBar() {
  const { data: me } = useGetMe();
  const location = useLocation();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const isKnowledgeBase = location.pathname.includes(
    PATHS.CONVERSATIONS_KNOWLEDGE_BASE
  );
  const isOnboarding = location.pathname.includes(
    PATHS.CONVERSATIONS_ONBOARDING
  );
  const isAgents = location.pathname.includes(PATHS.CONVERSATIONS_AGENTS);

  return (
    <header
      data-slot='inbox-system-bar'
      className='flex h-[72px] shrink-0 items-center justify-between border-b border-primary-100 bg-white px-6 py-4'
    >
      <div className='flex items-center gap-2'>
        <Messages1
          size={24}
          variant='Bulk'
          className='text-gray-1000'
          aria-hidden='true'
        />
        <h1 className='text-xl! font-semibold! text-gray-1000'>
          Conversations
        </h1>
      </div>

      <div className='flex h-10 items-center gap-2'>
        {isKnowledgeBase || isOnboarding ? (
          <Button
            type='button'
            variant='primaryPale'
            size='sm'
            className='border-0'
            aria-label='Add knowledge base'
            onClick={() => setCreateOpen(true)}
          >
            <Add
              size={20}
              aria-hidden='true'
            />
            Add knowledge base
          </Button>
        ) : null}
        {isAgents ? (
          <Button
            type='button'
            variant='primaryPale'
            size='sm'
            className='border-0'
            aria-label='Add Agent'
            disabled={!canManageAgents(me)}
            title={canManageAgents(me) ? undefined : MANAGE_AGENTS_HINT}
            onClick={() => setCreateOpen(true)}
          >
            <Add
              size={20}
              aria-hidden='true'
            />
            Add Agent
          </Button>
        ) : null}
        {/* No action yet.
        <Button
          type='button'
          variant='primary'
          size='sm'
          className='h-10 rounded-xl px-4 [&_svg]:size-5'
          aria-label='Add Broadcast'
        >
          <Add
            size={20}
            aria-hidden='true'
          />
          Add Broadcast
        </Button>
        <Button
          type='button'
          variant='ghost'
          size='iconSm'
          className='size-10 rounded-xl border border-primary-200 text-gray-800 [&_svg]:size-6'
          aria-label='More options'
        >
          <EllipsisVertical
            size={24}
            aria-hidden='true'
          />
        </Button>
        */}
      </div>

      {isKnowledgeBase || isOnboarding ? (
        <KbaseFormDialog
          kbase={null}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSaved={(saved) => {
            if (isOnboarding) {
              navigate(
                createRoute(PATHS.CONVERSATIONS_KNOWLEDGE_BASE_DETAIL(saved.id))
              );
              return;
            }
            const params = new URLSearchParams(location.search);
            params.delete('search');
            params.delete('tab');
            const search = params.toString();
            const listPath = knowledgeBaseListPath(location.pathname);
            navigate({
              pathname: `${listPath}/${saved.id}`,
              search: search ? `?${search}` : '',
            });
          }}
        />
      ) : null}
      {isAgents ? (
        <AgentCreateDialog
          agent={null}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSaved={(saved) => {
            const params = new URLSearchParams(location.search);
            const search = params.toString();
            const listPath = agentsListPath(location.pathname);
            navigate({
              pathname: `${listPath}/${saved.id}`,
              search: search ? `?${search}` : '',
            });
          }}
        />
      ) : null}
    </header>
  );
}
